import { classifyControllerStatus, classifyDeviceStatus } from "utils/deviceStatus";
import { formatRelativeTime } from "utils/helper";

// Four compact status chips: the greenhouse controller itself (its own
// is_online/last_status_at - the ESP32's own MQTT liveness), the two Tuya
// devices it controls (itc308_reachable/ihc200_reachable - a completely
// separate signal, since is_online says nothing about whether the ESP32
// can actually reach either Tuya device over local WiFi - found for real
// 2026-09-24), and the selected PadiHAT water-level device (its last_seen
// field, same as DevicesOverview/Dashboard). All lean on the shared
// utils/deviceStatus classifier so this never becomes a fifth,
// independently-drifting copy of the online-window thresholds.
//
// Deliberately NOT a 5th "pump" chip here: the pump has no network link
// separate from the ESP32 itself to lose - there's nothing for it to be
// "reachable" or not, distinct from the controller chip above. Its actual
// operational state (running/stopped/direction/reason) is already fully
// covered by PumpControlPanel's own status card - a "Pump: online" chip
// here would just always mirror the controller chip, not add information.
function StatusChip({ label, name, mac, status, lastUpdateValue }) {
  return (
    <div
      className="flex min-w-0 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2"
      data-testid={`status-chip-${label}`}
    >
      <span
        className={`h-2.5 w-2.5 shrink-0 rounded-full ${status ? status.dotClassName : "bg-slate-300"}`}
        aria-hidden="true"
      />
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
        <p className="truncate text-sm font-semibold text-slate-800">{name || mac || "—"}</p>
        <p className={`text-xs ${status ? status.textClassName : "text-slate-400"}`}>
          {status ? status.label : "No device selected"}
          {status && lastUpdateValue ? ` · ${formatRelativeTime(lastUpdateValue)}` : ""}
        </p>
      </div>
    </div>
  );
}

export default function RunContextBar({ controller, waterDevice }) {
  const controllerStatus = controller ? classifyControllerStatus(controller) : null;
  const itc308Status = controller
    ? classifyControllerStatus({
        is_online: controller.itc308_reachable,
        last_status_at: controller.itc308_status_at,
      })
    : null;
  const ihc200Status = controller
    ? classifyControllerStatus({
        is_online: controller.ihc200_reachable,
        last_status_at: controller.ihc200_status_at,
      })
    : null;
  const waterStatus = waterDevice ? classifyDeviceStatus(waterDevice.last_seen) : null;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <StatusChip
        label="Greenhouse controller"
        name={controller?.name}
        mac={controller?.mac_slug}
        status={controllerStatus}
        lastUpdateValue={controller?.last_status_at}
      />
      <StatusChip
        label="Temp controller (ITC-308)"
        name={controller ? "ITC-308" : null}
        status={itc308Status}
        lastUpdateValue={controller?.itc308_status_at}
      />
      <StatusChip
        label="Humidity controller (IHC-200)"
        name={controller ? "IHC-200" : null}
        status={ihc200Status}
        lastUpdateValue={controller?.ihc200_status_at}
      />
      <StatusChip
        label="PadiHAT device"
        name={waterDevice?.name}
        mac={waterDevice?.mac_address}
        status={waterStatus}
        lastUpdateValue={waterDevice?.last_seen}
      />
    </div>
  );
}
